/**
 * TransferPrimaryModal — Transfer primary cardholder role to another member.
 *
 * Shows a confirmation step since this is a destructive-ish action
 * (current user loses primary status).
 */

import { useState } from 'react';
import { Crown, AlertTriangle, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useBuddyGroupMutations } from '@/hooks/useBuddyGroupMembers';
import type { BuddyGroupMemberView } from '@/types/buddy-groups';

interface TransferPrimaryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groupId: string;
  members: BuddyGroupMemberView[];
  currentUserProfileId: string;
}

export function TransferPrimaryModal({
  open,
  onOpenChange,
  groupId,
  members,
  currentUserProfileId,
}: TransferPrimaryModalProps) {
  const { transferPrimary } = useBuddyGroupMutations();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Only accepted (non-pending) members can receive primary role
  const eligibleMembers = members.filter(
    (m) => m.user_profile_id !== currentUserProfileId && m.invitation_status !== 'pending'
  );

  const handleTransfer = async () => {
    if (!selectedId) return;
    setError(null);
    try {
      await transferPrimary.mutateAsync({
        groupId,
        userProfileId: selectedId,
      });
      onOpenChange(false);
      setSelectedId(null);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to transfer primary role');
    }
  };

  const handleClose = () => {
    if (!transferPrimary.isPending) {
      setSelectedId(null);
      setError(null);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Crown className="h-5 w-5 text-amber-500" />
            Transfer Primary Cardholder
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="flex items-start gap-2 rounded-lg bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-300">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <p>
              You will lose primary cardholder privileges. The new primary will
              be able to add/remove members and manage this group.
            </p>
          </div>

          {eligibleMembers.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">
              No other members to transfer to. Add members first.
            </p>
          ) : (
            <div className="space-y-2">
              <p className="text-sm font-medium">Select new primary:</p>
              {eligibleMembers.map((m) => (
                <label
                  key={m.user_profile_id}
                  className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                    selectedId === m.user_profile_id
                      ? 'border-primary bg-primary/5'
                      : 'hover:bg-accent/30'
                  }`}
                >
                  <input
                    type="radio"
                    name="transfer-primary"
                    value={m.user_profile_id}
                    checked={selectedId === m.user_profile_id}
                    onChange={() => setSelectedId(m.user_profile_id)}
                    className="accent-primary"
                  />
                  <span className="text-sm">
                    {m.display_name ?? m.user_profile_id.slice(0, 8)}
                  </span>
                </label>
              ))}
            </div>
          )}

          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={transferPrimary.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleTransfer}
            disabled={!selectedId || transferPrimary.isPending}
          >
            {transferPrimary.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Crown className="h-4 w-4 mr-2" />
            )}
            Transfer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
